import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  limit,
  where,
  serverTimestamp 
} from 'firebase/firestore';

// Resume Management
export const saveResume = async (userId, resumeData) => {
  try {
    if (!db.collection && !collection) {
      // Demo mode - use localStorage
      const resumes = JSON.parse(localStorage.getItem(`resumes_${userId}`) || '[]');
      const newResume = {
        id: Date.now().toString(),
        ...resumeData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      resumes.push(newResume);
      localStorage.setItem(`resumes_${userId}`, JSON.stringify(resumes));
      return newResume.id;
    }

    // Real Firebase
    const resumeRef = doc(collection(db, 'users', userId, 'resumes'));
    const resumeDoc = {
      ...resumeData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(resumeRef, resumeDoc);
    return resumeRef.id;
  } catch (error) {
    console.error('Error saving resume:', error);
    throw error;
  }
};

export const getUserResumes = async (userId) => {
  try {
    if (!db.collection && !collection) {
      // Demo mode
      const resumes = JSON.parse(localStorage.getItem(`resumes_${userId}`) || '[]');
      return resumes;
    }

    // Real Firebase
    const resumesRef = collection(db, 'users', userId, 'resumes');
    const q = query(resumesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user resumes:', error);
    throw error;
  }
};

export const deleteResume = async (userId, resumeId) => {
  try {
    if (!db.collection && !collection) {
      // Demo mode
      const resumes = JSON.parse(localStorage.getItem(`resumes_${userId}`) || '[]');
      const updatedResumes = resumes.filter(resume => resume.id !== resumeId);
      localStorage.setItem(`resumes_${userId}`, JSON.stringify(updatedResumes));
      return;
    }

    // Real Firebase
    const resumeRef = doc(db, 'users', userId, 'resumes', resumeId);
    await deleteDoc(resumeRef);
  } catch (error) {
    console.error('Error deleting resume:', error);
    throw error;
  }
};

// User Stats Management
export const updateUserStats = async (userId, stats) => {
  try {
    if (!db.collection && !collection) {
      // Demo mode
      const profile = JSON.parse(localStorage.getItem(`user_profile_${userId}`) || '{}');
      profile.stats = { ...profile.stats, ...stats };
      localStorage.setItem(`user_profile_${userId}`, JSON.stringify(profile));
      return;
    }

    // Real Firebase
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      stats: stats,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user stats:', error);
    throw error;
  }
};

export const getUserStats = async (userId) => {
  try {
    if (!db.collection && !collection) {
      // Demo mode
      const profile = JSON.parse(localStorage.getItem(`user_profile_${userId}`) || '{}');
      return profile.stats || {
        totalResumes: 0,
        totalDownloads: 0,
        averageScore: 0,
        rank: Math.floor(Math.random() * 100) + 1,
        badges: []
      };
    }

    // Real Firebase
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data().stats || {};
    } else {
      return {
        totalResumes: 0,
        totalDownloads: 0,
        averageScore: 0,
        rank: Math.floor(Math.random() * 100) + 1,
        badges: []
      };
    }
  } catch (error) {
    console.error('Error getting user stats:', error);
    throw error;
  }
};

// Leaderboard Management
export const getLeaderboard = async (limitCount = 10) => {
  try {
    if (!db.collection && !collection) {
      // Demo mode - return mock leaderboard
      return [
        { uid: '1', displayName: 'Sarah Johnson', stats: { averageScore: 98, totalResumes: 12 } },
        { uid: '2', displayName: 'Michael Chen', stats: { averageScore: 95, totalResumes: 8 } },
        { uid: '3', displayName: 'Emily Rodriguez', stats: { averageScore: 92, totalResumes: 15 } },
        { uid: '4', displayName: 'David Kim', stats: { averageScore: 89, totalResumes: 6 } },
        { uid: '5', displayName: 'Lisa Thompson', stats: { averageScore: 87, totalResumes: 9 } }
      ];
    }

    // Real Firebase
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef, 
      orderBy('stats.averageScore', 'desc'), 
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting leaderboard:', error);
    throw error;
  }
};

// Badge System
export const awardBadge = async (userId, badgeName) => {
  try {
    const userStats = await getUserStats(userId);
    const currentBadges = userStats.badges || [];
    
    if (!currentBadges.includes(badgeName)) {
      const updatedBadges = [...currentBadges, badgeName];
      await updateUserStats(userId, { 
        ...userStats, 
        badges: updatedBadges 
      });
      return true; // Badge was awarded
    }
    
    return false; // Badge already exists
  } catch (error) {
    console.error('Error awarding badge:', error);
    throw error;
  }
};

export const checkAndAwardBadges = async (userId, resumeScore, totalResumes) => {
  const badges = [];
  
  try {
    // First Resume badge
    if (totalResumes === 1) {
      const awarded = await awardBadge(userId, 'First Resume');
      if (awarded) badges.push('First Resume');
    }
    
    // Score Master badge (90+ score)
    if (resumeScore >= 90) {
      const awarded = await awardBadge(userId, 'Score Master');
      if (awarded) badges.push('Score Master');
    }
    
    // Perfectionist badge (100% score)
    if (resumeScore >= 100) {
      const awarded = await awardBadge(userId, 'Perfectionist');
      if (awarded) badges.push('Perfectionist');
    }
    
    // Template Explorer badge (used all 3 templates)
    // This would need additional tracking in real implementation
    
    // Consistent User badge (5 resumes)
    if (totalResumes >= 5) {
      const awarded = await awardBadge(userId, 'Consistent User');
      if (awarded) badges.push('Consistent User');
    }
    
    return badges; // Return newly awarded badges
  } catch (error) {
    console.error('Error checking badges:', error);
    return [];
  }
};

// Analytics
export const saveResumeAnalytics = async (userId, analyticsData) => {
  try {
    if (!db.collection && !collection) {
      // Demo mode
      const analytics = JSON.parse(localStorage.getItem(`analytics_${userId}`) || '[]');
      analytics.push({
        ...analyticsData,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem(`analytics_${userId}`, JSON.stringify(analytics));
      return;
    }

    // Real Firebase
    const analyticsRef = doc(collection(db, 'users', userId, 'analytics'));
    await setDoc(analyticsRef, {
      ...analyticsData,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error saving analytics:', error);
    throw error;
  }
};

export const getUserAnalytics = async (userId) => {
  try {
    if (!db.collection && !collection) {
      // Demo mode
      const analytics = JSON.parse(localStorage.getItem(`analytics_${userId}`) || '[]');
      return analytics;
    }

    // Real Firebase
    const analyticsRef = collection(db, 'users', userId, 'analytics');
    const q = query(analyticsRef, orderBy('timestamp', 'desc'), limit(50));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user analytics:', error);
    throw error;
  }
};